import dtfIndexAbi from '@/abis/dtf-index-abi'
import { Button } from '@/components/ui/button'
import { indexDTFAtom } from '@/state/dtf/atoms'
import { parseDuration, parseDurationShort } from '@/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { atom, useAtom, useAtomValue } from 'jotai'
import { LoaderCircle, MousePointerBan } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  usePublicClient,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi'
import { currentRebalanceAtom } from '../../../atoms'
import {
  isAuctionOngoingAtom,
  rebalanceAuctionsAtom,
  rebalancePercentAtom,
  refreshNonceAtom,
} from '../atoms'
import useRebalanceParams from '../hooks/use-rebalance-params'
import Help from '@/components/ui/help'
import useCurrentTime from '@/hooks/useCurrentTime'
import {
  wouldAuctionOverlapFeeHandout,
  wouldAuctionOverlapFeeHandoutNow,
} from '../utils/auction-fee-handout-overlap'

const auctionNumberAtom = atom((get) => {
  const auctions = get(rebalanceAuctionsAtom)
  return auctions.length + 1
})

const CommunityLaunchAuctionsButton = () => {
  const { t } = useLingui()
  const dtf = useAtomValue(indexDTFAtom)
  const rebalance = useAtomValue(currentRebalanceAtom)
  const rebalancePercent = useAtomValue(rebalancePercentAtom)
  const rebalanceParams = useRebalanceParams()
  const [refreshNonce, setRefreshNonce] = useAtom(refreshNonceAtom)
  const auctionNumber = useAtomValue(auctionNumberAtom)
  const [isLaunching, setIsLaunching] = useState(false)
  const { writeContract, isError, isPending, data } = useWriteContract()
  const publicClient = usePublicClient({ chainId: dtf?.chainId })
  const { isSuccess } = useWaitForTransactionReceipt({
    hash: data,
    chainId: dtf?.chainId,
  })
  const [error, setError] = useState<string | null>(null)
  const isAuctionOngoing = useAtomValue(isAuctionOngoingAtom)
  const currentTime = useCurrentTime()
  const restrictedUntil = rebalance
    ? Number(rebalance.rebalance.restrictedUntil)
    : 0
  const isRestrictedPeriod = !!rebalance && restrictedUntil > currentTime
  const countdown = Math.max(0, restrictedUntil - currentTime)
  const isFeeHandoutOverlap =
    !!dtf && wouldAuctionOverlapFeeHandout(currentTime, dtf.auctionLength)
  const isValid =
    !!rebalanceParams &&
    rebalancePercent > 0 &&
    rebalance &&
    dtf &&
    !isFeeHandoutOverlap
  const isNotCommunityLaunch =
    rebalance?.rebalance.availableUntil === rebalance?.rebalance.restrictedUntil

  useEffect(() => {
    if (isSuccess) {
      setError(null)
      // Refresh nonce after 10s
      let timeout = setTimeout(() => {
        setRefreshNonce(refreshNonce + 1)
      }, 1000 * 10)
      // Remove loading after 15s
      let launchTimeout = setTimeout(() => {
        setIsLaunching(false)
      }, 1000 * 15)

      return () => {
        clearTimeout(timeout)
        clearTimeout(launchTimeout)
      }
    }
  }, [isSuccess])

  const handleStartAuctions = async () => {
    if (
      !isValid ||
      !dtf ||
      !publicClient ||
      !rebalanceParams
    )
      return

    try {
      setIsLaunching(true)
      setError(null)

      const auctionLength = Number(
        await publicClient.readContract({
          address: dtf.id,
          abi: dtfIndexAbi,
          functionName: 'auctionLength',
        })
      )
      if (wouldAuctionOverlapFeeHandoutNow(auctionLength)) {
        setIsLaunching(false)
        setError(
          t`Cannot launch: auction would overlap the daily TVL fee handout`
        )
        return
      }

      writeContract({
        address: dtf.id,
        abi: dtfIndexAbi,
        functionName: 'openAuctionUnrestricted',
        args: [BigInt(rebalance.rebalance.nonce)],
        chainId: dtf?.chainId,
      })
    } catch (e) {
      console.error('Error opening auction', e)
      setIsLaunching(false)
      setError(t`Error opening auctions`)
    }
  }

  if (isNotCommunityLaunch) {
    return (
      <div className="flex gap-2 items-center justify-center p-6 text-center">
        <div className="text-sm text-muted-foreground">
          <Trans>Community launch is not available for this rebalance</Trans>
        </div>
        <Help content={t`Only the auction launcher can start auctions`} />
      </div>
    )
  }

  if (isRestrictedPeriod && countdown > 0) {
    return (
      <div className="flex flex-col gap-2 p-2 text-center">
        <Button className="rounded-xl w-full py-6 gap-2" disabled={true}>
          <MousePointerBan size={14} strokeWidth={1.5} />
          <span className="text-sm text-muted-foreground">
            <Trans>
              Permissionless in:{' '}
              <span className="font-bold ">
                {parseDurationShort(countdown, { round: true })
                  .replaceAll(' ', '')
                  .replaceAll(',', ' ')}
              </span>
            </Trans>
          </span>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      {isFeeHandoutOverlap && (
        <p
          data-testid="auctions-fee-handout-overlap"
          className="text-center text-sm text-destructive px-2"
        >
          <Trans>
            Cannot launch: auction would overlap the daily TVL fee handout
          </Trans>
        </p>
      )}
      <Button
        data-testid="auctions-community-launch-btn"
        className="rounded-xl w-full py-6 gap-2"
        disabled={
          !isValid ||
          isPending ||
          isAuctionOngoing ||
          isLaunching ||
          isRestrictedPeriod ||
          isFeeHandoutOverlap
        }
        onClick={handleStartAuctions}
      >
        {isPending || isAuctionOngoing || isLaunching ? (
          <>
            <LoaderCircle size={16} className="animate-spin" />
            <span>
              {isAuctionOngoing ? (
                <Trans>Rebalance ongoing</Trans>
              ) : (
                <Trans>Launching...</Trans>
              )}
            </span>
          </>
        ) : (
          <>
            <span>
              <Trans>Start auction {auctionNumber}</Trans>
            </span>
            <span className="font-light">
              ({parseDuration(dtf?.auctionLength ?? 0, { round: true })})
            </span>
          </>
        )}
      </Button>
      {error && (
        <div data-testid="auctions-launch-error" className="text-red-500">
          {error}
        </div>
      )}
    </div>
  )
}

export default CommunityLaunchAuctionsButton
